import { Injectable } from '@angular/core';
import {
  AnimationContext,
  AnimationPreferences,
  GraphicsContext,
  SceneContext,
  StudioAnimation,
  StudioTextAnimationStep,
  WordAnimationStep,
} from '../..';
import { StudioSceneShape } from '../../../../shape/shapes/general/root/studio-scene-shape';
import {
  LineAnimation,
  TranslateAnimation,
} from '../../../../animation/text-animation.service';

@Injectable({ providedIn: 'root' })
export class StudioTextAnimationService {
  textAnimations: Record<string, StudioTextAnimationStep> = {
    'zoom-out-and-letter-fade-in': {
      duration: 1,
      type: 'appear',
      line: {
        letter: {
          overlap: 0.2,
        },
      },
      zoom: {
        from: 0.5,
        to: 1.4,
      },
    } as StudioTextAnimationStep,
    'line-from-right': {
      duration: 1,
      word: {
        translate: {
          from: [0, 40],
        } as TranslateAnimation,
      } as WordAnimationStep,
      line: {
        translate: {
          from: [200, 0],
        },
      } as LineAnimation,
    },
    // Multi-line animations with lineIndex and nextLine
    'case-1': {
      duration: 1,
      type: 'appear',
      lineIndex: 0,
      line: {
        letter: {
          overlap: 0.85,
        },
      } as LineAnimation,
    },
    'case-2': {
      duration: 2,
      type: 'appear',
      translate: {
        from: [0, -60],
      },
      line: {
        endBefore: 1,
      } as LineAnimation,
    },
  };

  level1Families = ['section-appear', 'text-appear'];

  constructor() {}

  /**
   * Generate text animation based on context.
   * Uses text properties, scene role, and preferences to select the best animation.
   */
  getAnimation(context: AnimationContext): StudioAnimation | null {
    const { graphics, scene, preferences, animation } = context;

    // Validate that we have text content
    if (!graphics?.layout || graphics.layout.primaryElement !== 'text') {
      return null;
    }

    const textProps = graphics.text;
    const sceneRole = scene?.role;
    const pacing = preferences?.pacing || 'normal';

    // Determine the target text ID (first text in scene)
    const textId = 'text:0';
    // Select animation family based on context
    const animationKey = this.selectAnimationKey(
      textProps,
      sceneRole,
      pacing,
      animation,
    );
    // Build and return the animation //
    const newAnimation = this.buildAnimationFromKey(
      textId,
      animationKey,
      context,
    );
    newAnimation.meta = {
      key: animationKey,
    };
    return newAnimation;
  }

  /**
   * Select animation key based on text properties and scene context
   */
  private selectAnimationKey(
    textProps: GraphicsContext['text'],
    sceneRole: SceneContext['role'],
    pacing: AnimationPreferences['pacing'],
    currentAnimation?: StudioAnimation,
  ): string {
    const filterFunction = (key: string) => {
      if (currentAnimation?.meta?.key == key) {
        return false;
      }
      return true;
    };

    // Intro/conclusion scenes get special treatment
    // if (sceneRole === 'intro') {
    //   return 'section-appear:by-letter';
    // }
    // if (sceneRole === 'conclusion') {
    //   return 'zoom-out-and-letter-fade-in';
    // }
    // if (sceneRole === 'emphasis') {
    //   return 'section-appear:by-word';
    // }

    // For body scenes, use text properties to decide
    if (textProps) {
      // Short text (few words) - letter-based animation

      // Multi-line text (2-3 lines) - use line-specific animations
      if (textProps.lineCount >= 2) {
        const multiLineKeys = ['case-1', 'case-2'].filter(filterFunction);
        return this.getRandomFromArray(multiLineKeys);
      }
    }

    // Default: randomly select from available animations
    const keys = [
      'section-appear:by-word',
      'section-appear:by-letter',
      'zoom-out-and-letter-fade-in',
      'line-from-right',
      'lines-from-opposite',
    ].filter(filterFunction);

    return this.getRandomFromArray(keys);
  }

  /**
   * Build animation from the selected key
   */
  private buildAnimationFromKey(
    target: string,
    key: string,
    context: AnimationContext,
  ): StudioAnimation {
    const pacing = context.preferences?.pacing || 'normal';
    const durationMultiplier =
      pacing === 'slow' ? 1.3 : pacing === 'fast' ? 0.7 : 1;

    // Handle composite keys (e.g., 'section-appear:by-word')
    if (key.startsWith('section-appear:')) {
      const innerKey = key.split(':')[1] as 'by-word' | 'by-letter';
      const animation = this._getTextAnimation(
        target,
        'section-appear',
        innerKey,
      );
      if (animation) {
        animation.duration = animation.duration * durationMultiplier;
      }
      return animation;
    }

    // Handle preset animations
    const preset = this.textAnimations[key];
    if (preset) {
      const animation = this.applyTargetAndDuration(
        preset,
        target,
        durationMultiplier,
      );
      return animation;
    }

    // Fallback to zoom-out animation
    return {
      target,
      duration: 1 * durationMultiplier,
      type: 'appear',
      zoom: { from: 0.8, to: 1 },
    } as StudioAnimation;
  }

  /**
   * Recursively apply target and duration multiplier to animation and its nextLine chain
   */
  private applyTargetAndDuration(
    animation: StudioTextAnimationStep,
    target: string,
    durationMultiplier: number,
  ): StudioTextAnimationStep {
    const result: StudioTextAnimationStep = {
      ...animation,
      target,
      duration: (animation.duration || 1) * durationMultiplier,
    };

    // Recursively apply to nextLine chain
    if (animation.nextLine) {
      result.nextLine = this.applyTargetAndDuration(
        animation.nextLine,
        target,
        durationMultiplier,
      );
    }

    return result;
  }

  /**
   * Build a multi-line animation dynamically based on the number of lines.
   * Uses lineIndex and nextLine to chain animations for each line.
   */
  buildMultiLineAnimation(
    target: string,
    lineCount: number,
    options?: {
      durationPerLine?: number;
      overlap?: number;
      translateFrom?: [number, number];
      direction?: 'down' | 'up' | 'alternate';
    },
  ): StudioTextAnimationStep {
    const durationPerLine = options?.durationPerLine || 0.8;
    const overlap = options?.overlap || 0.5;
    const direction = options?.direction || 'down';

    const getTranslateFrom = (
      index: number,
    ): { from: [number, number] } | undefined => {
      if (options?.translateFrom) {
        return { from: options.translateFrom };
      }
      // Default translate based on direction
      switch (direction) {
        case 'down':
          return { from: [0, -30] };
        case 'up':
          return { from: [0, 30] };
        case 'alternate':
          return { from: [0, index % 2 === 0 ? 30 : -30] };
        default:
          return { from: [0, -30] };
      }
    };

    // Build the chain from the last line backwards
    let currentAnimation: StudioTextAnimationStep | undefined;

    for (let i = Math.min(lineCount - 1, 5); i >= 0; i--) {
      const lineAnimation: StudioTextAnimationStep = {
        target,
        duration: durationPerLine,
        type: 'appear',
        lineIndex: i,
        line: {
          translate: getTranslateFrom(i),
        } as LineAnimation,
      };

      if (currentAnimation) {
        lineAnimation.nextLine = {
          ...currentAnimation,
          overlap,
        };
      }

      currentAnimation = lineAnimation;
    }

    return currentAnimation;
  }

  getTextAnimation(sceneShape: StudioSceneShape): StudioAnimation {
    // In this case we assume we wil do the

    const [textId] = sceneShape.scene.texts || [];
    const textCell = sceneShape.shapeCells[textId];

    // -- // -- //

    const baseKeys = [
      'section-appear',
      'zoom-out-and-letter-fade-in',
      'line-from-right',
    ];

    const baseKey = this.getRandomFromArray(baseKeys);

    let animation: StudioAnimation = null;
    switch (baseKey) {
      case 'section-appear':
        const innterKeys = ['by-word', 'by-letter'];
        const innerKey = this.getRandomFromArray(innterKeys);
        return this._getTextAnimation(textId, 'section-appear', innerKey);

      case 'zoom-out-and-letter-fade-in':
        animation = this.textAnimations['zoom-out-and-letter-fade-in'];
        break;
      case 'line-from-right':
        animation = this.textAnimations['line-from-right'];
        break;
    }

    animation.target = textId;
    return animation;
  }

  _getTextAnimation(
    target: string,
    key: 'section-appear',
    innerKey: 'by-word' | 'by-letter',
  ) {
    console.log('getTextAnimation', key, innerKey);
    if (key == 'section-appear') {
      const base = {
        target,
        duration: 1,
        type: 'appear',
        line: {
          segment: [0, 1],
          align: 'static',
        },
        zoom: {
          from: 2,
          to: 1.2,
        },
      } as StudioTextAnimationStep;

      if (innerKey == 'by-word') {
        return {
          ...base,
          next: {
            target,
            duration: 1,
            word: {
              overlap: 0.4,
            },
            line: {
              segment: [1, undefined],
              align: 'dynamic',
            },
          },
        };
      }
      if (innerKey == 'by-letter') {
        return {
          ...base,
          next: {
            target,
            duration: 1,
            letter: {
              overlap: 0.9,
            },
            line: {
              segment: [1, undefined],
              align: 'dynamic',
            },
          },
        } as StudioTextAnimationStep;
      }
    }
  }

  getRandomFromArray(array: any[]) {
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
  }
}
